import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useCart } from "./CartContext";
import api from "./axiosConfig";
import {
  Heart,
  ShoppingCart,
  Loader,
  ChevronRight,
  Search,
  Phone,
  Clock,
  CreditCard,
  Store,
  ChevronLeft,
  MapPin, Map as MapIcon,
  Copy,   // ✅ AJOUT
  Check, // ✅ AJOUT
  Tag,
  Zap
} from "lucide-react";

// Identité visuelle Souqi harmonisée
const ACCENT_COLOR_CLASS = `text-[#f34141]`;
const BUTTON_BG_CLASS = `bg-gradient-to-r from-[#f34141] to-[#ff6b6b]`;

export default function MerchantCatalogue() {
  const { id: merchantId } = useParams();
  const { addToCart } = useCart();
  const phoneClient = localStorage.getItem("customerPhone");

  // --- ÉTATS ---
  const [merchant, setMerchant] = useState(null);
  const [produits, setProduits] = useState([]);
  
  const [isMerchantLoading, setIsMerchantLoading] = useState(true);
  const [isProductsLoading, setIsProductsLoading] = useState(true);

  const [isFavorite, setIsFavorite] = useState(false);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("Toutes");
  const [availableCategories, setAvailableCategories] = useState(["Toutes"]);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(24);
  const [totalPages, setTotalPages] = useState(1);

  // --- EFFETS ---

  // 1. Reset page et scroll
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page, activeCategory, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, activeCategory, merchantId]);

  // 2. Charger les infos du marchand (Nom, Tel, Horaires...)
  useEffect(() => {
    if (!merchantId) return;
    setIsMerchantLoading(true);
    fetch(`https://souqi-api.onrender.com/api/merchants/public/${merchantId}`)
      .then((r) => r.json())
      .then((data) => {
        if (data?.merchant) setMerchant(data.merchant);
      })
      .catch((e) => console.error("Erreur marchand", e))
      .finally(() => setIsMerchantLoading(false));
  }, [merchantId]);

  // 3. CHARGEMENT PRINCIPAL : PRODUITS + CATÉGORIES
  // C'est ici que le problème est résolu. On lit data.categories.
  useEffect(() => {
    if (!merchantId) return;

    setIsProductsLoading(true);
    
    const qs = new URLSearchParams();
    qs.set("page", String(page));
    qs.set("limit", String(limit));
    if (searchTerm.trim()) qs.set("search", searchTerm.trim());
    
    // Si on a sélectionné une catégorie spécifique (autre que Toutes), on filtre
    if (activeCategory !== "Toutes") qs.set("category", activeCategory);

    fetch(`https://souqi-api.onrender.com/api/products/public/${merchantId}?${qs.toString()}`)
      .then((r) => r.json())
      .then((data) => {
        // A. Mise à jour des produits
        const rawProducts = data.products || [];
        const normalizedProducts = rawProducts.map((p) => ({
          ...p,
          category: p.category || "Autres",
          outOfStock: Boolean(p.outOfStock),
        }));
        setProduits(normalizedProducts);
        setTotalPages(Number(data.totalPages || 1));

        // B. ✅ LE FIX : On récupère les catégories envoyées par le backend
        // On ne met à jour la liste que si on reçoit des données et qu'on n'est pas en train de chercher/filtrer
        // (pour éviter que les onglets disparaissent quand on clique dessus)
        if (data.categories && Array.isArray(data.categories) && data.categories.length > 0) {
           // On garde toujours "Toutes" au début
           const uniqueCats = ["Toutes", ...data.categories];
           // On met à jour uniquement si c'est la première charge ou si on est en mode "vue globale"
           // Cela empêche un bug visuel où les onglets changeraient pendant qu'on navigue
           if (availableCategories.length <= 1 || (activeCategory === "Toutes" && !searchTerm)) {
               setAvailableCategories(uniqueCats);
           }
        }
      })
      .catch((err) => {
        console.error("Erreur chargement produits", err);
        setProduits([]);
      })
      .finally(() => setIsProductsLoading(false));

  }, [merchantId, page, limit, searchTerm, activeCategory]);

  // 4. Gestion Favoris
  useEffect(() => {
    if (!phoneClient || !merchantId) {
      setFavoritesLoaded(true);
      return;
    }
    api.get(`/users/client/${phoneClient}/favorites`)
      .then((res) => {
        const favorites = res.data.favorites || [];
        setIsFavorite(favorites.some((m) => String(m._id) === String(merchantId)));
      })
      .catch(() => {})
      .finally(() => setFavoritesLoaded(true));
  }, [phoneClient, merchantId]);

  const toggleFavorite = async () => {
    if (!phoneClient || !merchantId) return;
    try {
      if (isFavorite) {
        await api.delete(`/users/client/${phoneClient}/favorites/${merchantId}`);
        setIsFavorite(false);
      } else {
        await api.post(`/users/client/${phoneClient}/favorites/${merchantId}`);
        setIsFavorite(true);
      }
    } catch {}
  };

  const getProductImageUrl = (image, width = 500) => {
    if (!image) return "";
    
    // Déterminer l'URL de base
    let url = image.startsWith("http") 
      ? image 
      : `https://souqi-api.onrender.com${image.startsWith("/") ? "" : "/"}${image}`;

    // Appliquer l'optimisation Cloudinary si c'est une URL Cloudinary
    if (url.includes("res.cloudinary.com")) {
      // f_auto : Format automatique (WebP/AVIF au lieu de JPG)
      // q_auto : Qualité automatique (compression sans perte visible)
      // w_...  : Redimensionnement à la largeur demandée
      // c_limit: Évite d'agrandir l'image si elle est déjà plus petite que la largeur
      return url.replace("/upload/", `/upload/f_auto,q_auto,w_${width},c_limit/`);
    }

    return url;
  };

  const resolvedMerchantName = merchant?.name || merchant?.storeName || "Supermarché";

  // --- ZONE À REMPLACER (Lignes ~118 à ~150 environ) ---

  const formattedOpeningHours = (() => {
    if (!merchant?.openingHours) return null;
    const daysMap = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const now = new Date();
    const todayKey = daysMap[now.getDay()];
    const today = merchant.openingHours[todayKey];

    if (today) {
      if (today.isClosed) return <span className="text-red-600 font-extrabold">Fermé aujourd'hui</span>;
      if (today.open && today.close) return `Aujourd'hui : ${today.open} – ${today.close}`;
    }
    return null;
  })();

  const isClosed = (() => {
    if (!merchant?.openingHours) return false;
    
    const now = new Date();
    const daysMap = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    // 1. Vérification pour AUJOURD'HUI
    const todayKey = daysMap[now.getDay()];
    const today = merchant.openingHours[todayKey];
    let isOpenToday = false;

    if (today && !today.isClosed && today.open && today.close) {
      if (today.close > today.open) {
        // Cas standard (ex: 08:00 à 22:00)
        isOpenToday = currentTime >= today.open && currentTime <= today.close;
      } else {
        // Cas nuit (ex: 07:00 à 03:00)
        isOpenToday = currentTime >= today.open || currentTime <= today.close;
      }
    }

    // 2. Vérification pour HIER (au cas où on est après minuit)
    const yesterdayIdx = (now.getDay() + 6) % 7;
    const yesterdayKey = daysMap[yesterdayIdx];
    const yesterday = merchant.openingHours[yesterdayKey];
    let isOpenFromYesterday = false;

    if (yesterday && !yesterday.isClosed && yesterday.open && yesterday.close) {
      if (yesterday.close < yesterday.open) {
        // Si hier fermait après minuit, on vérifie si on est encore dans ce créneau
        isOpenFromYesterday = currentTime <= yesterday.close;
      }
    }

    return !(isOpenToday || isOpenFromYesterday);
  })();

// --- FIN DE LA ZONE À REMPLACER ---

  if (isMerchantLoading || !favoritesLoaded) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#FAFAFA]">
        <Loader size={40} className={`animate-spin text-[#f34141]`} />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 pb-20 font-sans pt-28 bg-[#FAFAFA]">
      <div className="mx-auto max-w-7xl">
        {/* EN-TÊTE MARCHAND */}
        <div className="flex flex-col gap-6 mb-12 md:flex-row md:items-end md:justify-between animate-fade-up">
          <div>
            <div className="flex items-center gap-2 mb-3 text-sm font-bold tracking-widest text-gray-400 uppercase">
              <Store size={16} />
              <span>Catalogue Officiel</span>
            </div>
            <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 md:text-6xl">
              {resolvedMerchantName}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {phoneClient && (
              <button
                onClick={toggleFavorite}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold transition-all shadow-sm border ${
                  isFavorite ? "bg-red-50 text-[#f34141] border-red-100" : "bg-white text-gray-400 border-gray-100"
                }`}
              >
                <Heart size={20} fill={isFavorite ? "currentColor" : "none"} />
                <span>{isFavorite ? "Favori" : "Enregistrer"}</span>
              </button>
            )}
          </div>
        </div>

        {/* CARTES INFOS */}
        <div className="grid gap-6 mb-12 sm:grid-cols-2 lg:grid-cols-3">
          {merchant?.phone && (
            <InfoCard icon={<Phone size={18} className="text-blue-500" />} label="Contact">
              {merchant.phone}
            </InfoCard>
          )}
          {formattedOpeningHours && (
            <InfoCard icon={<Clock size={18} className="text-orange-500" />} label="Horaires">
              {formattedOpeningHours}
            </InfoCard>
          )}
          
          {/* --- NOUVELLE CARTE : MINI-MAP --- */}
          {merchant?.locationUrl && (
            <button 
              onClick={() => window.open(merchant.locationUrl, '_blank')}
              className="group relative flex items-center gap-4 p-5 bg-white border border-gray-100 shadow-sm rounded-3xl overflow-hidden hover:border-red-200 transition-all text-left"
            >
              <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
                <svg width="100%" height="100%">
                  <rect width="100%" height="100%" fill="url(#grid)" />
                  <defs>
                    <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                      <path d="M 20 0 L 0 0 0 20" fill="none" stroke="black" strokeWidth="1"/>
                    </pattern>
                  </defs>
                </svg>
              </div>
              <div className="relative z-10 w-12 h-12 flex items-center justify-center bg-red-50 text-[#f34141] rounded-2xl group-hover:scale-110 transition-transform">
                <MapPin size={22} fill="currentColor" fillOpacity={0.2} />
              </div>
              <div className="relative z-10">
                <p className="text-xs font-bold text-gray-400 uppercase">Localisation</p>
                <div className="flex items-center gap-1 text-sm font-black text-slate-800">
                  Voir sur Maps <ChevronRight size={14} className="text-[#f34141]" />
                </div>
              </div>
            </button>
          )}

          {Array.isArray(merchant?.paymentMethods) && merchant.paymentMethods.length > 0 && (
            <InfoCard icon={<CreditCard size={18} className="text-emerald-500" />} label="Paiement">
              <div className="flex flex-wrap gap-2 mt-1">
                {merchant.paymentMethods.map((p, i) => {
                  const isString = typeof p === 'string';
                  const nomPaiement = isString ? p : (p.label || p.method || p.name || "");
                  const numero = isString ? "" : (p.value || p.phone || p.number || "");
                  return (
                    <PaymentBadge 
                      key={i} 
                      nomPaiement={nomPaiement} 
                      numero={numero} 
                    />
                  ); 
                })}
              </div>
            </InfoCard>
          )}
        </div>

        {/* BARRE DE RECHERCHE & CATÉGORIES */}
        <div className="sticky top-24 z-40 py-4 mb-10 bg-[#FAFAFA]/90 backdrop-blur-md">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative flex items-center w-full max-w-xl bg-white border border-gray-100 shadow-sm rounded-2xl group focus-within:border-red-100">
              <Search size={20} className="absolute ml-5 text-gray-400 group-focus-within:text-[#f34141]" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Chercher..."
                className="w-full py-4 pr-6 text-lg bg-transparent outline-none pl-14 rounded-2xl text-slate-700"
              />
            </div>
            
            <div className="flex gap-2 pb-2 overflow-x-auto lg:pb-0 hide-scrollbar">
              {availableCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`whitespace-nowrap px-6 py-3 rounded-xl text-sm font-bold transition-all shadow-sm ${
                    activeCategory === cat
                      ? `${BUTTON_BG_CLASS} text-white shadow-red-500/20`
                      : "bg-white text-gray-500 border border-gray-100 hover:text-red-500"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* GRID PRODUITS */}
        {isProductsLoading ? (
          <div className="flex justify-center py-20">
            <Loader size={32} className="text-[#f34141] animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {produits.map((p) => {
                const currentPrice = Number(p.price);
                const basePrice = p.originalPrice ? Number(p.originalPrice) : null;
                const hasDiscount = basePrice && basePrice > currentPrice;
                const discountPercent = hasDiscount ? Math.round(((basePrice - currentPrice) / basePrice) * 100) : 0;

                return (
                  <div key={p._id} className="relative flex flex-col p-4 bg-white border border-gray-100 shadow-sm rounded-[2.5rem] hover:shadow-xl transition-all group overflow-hidden">
                    {hasDiscount && !p.outOfStock && (
                      <div className="absolute top-4 left-4 z-20 flex items-center gap-1 bg-[#f34141] text-white px-3 py-1.5 rounded-2xl shadow-lg animate-pulse">
                        <Zap size={12} fill="white" />
                        <span className="text-[10px] font-black uppercase tracking-tighter">-{discountPercent}%</span>
                      </div>
                    )}
                    
                    <div className="relative h-48 mb-6 overflow-hidden bg-gray-50 rounded-3xl">
                      <img 
                        src={getProductImageUrl(p.image, 400)} // On demande du 400px pour la grille
                        alt={p.name} 
                        className="object-contain w-full h-full p-4 transition-transform duration-700 mix-blend-multiply group-hover:scale-110" 
                        loading="lazy" // Ajoute ceci pour ne charger que les images visibles à l'écran
                      />
                      {p.outOfStock && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[2px]">
                          <span className="px-3 py-1 text-xs font-black text-white uppercase bg-slate-800 rounded-lg">Rupture</span>
                        </div>
                      )}
                    </div>

                    <div className="px-2 mb-6">
                      <h2 className="text-lg font-bold text-slate-900 line-clamp-1 group-hover:text-[#f34141]">{p.name}</h2>
                      <div className="flex flex-col mt-3">
                        {hasDiscount ? (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-slate-300 line-through">{basePrice} MRU</span>
                              <span className="text-[10px] font-black text-[#f34141] bg-red-50 px-2 py-0.5 rounded-lg">PROMO</span>
                            </div>
                            <div className="flex items-baseline gap-1">
                              <span className="text-3xl font-black text-[#f34141] tracking-tighter">{currentPrice}</span>
                              <span className="text-xs font-bold text-[#f34141]/60 uppercase">MRU</span>
                            </div>
                          </>
                        ) : (
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-black text-slate-900 tracking-tighter">{currentPrice}</span>
                            <span className="text-xs font-bold text-slate-400 uppercase">MRU</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      disabled={p.outOfStock || isClosed}
                      onClick={() => {
                        if (isClosed) { alert("Fermé !"); return; }
                        addToCart({ ...p, price: Number(p.price), merchantId, merchantName: resolvedMerchantName });
                      }}
                      className={`w-full py-4 text-white rounded-[1.5rem] font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 ${
                        p.outOfStock || isClosed ? "bg-gray-200 cursor-not-allowed shadow-none" : `${BUTTON_BG_CLASS} shadow-red-500/20`
                      }`}
                    >
                      <ShoppingCart size={14} />
                      <span>{p.outOfStock ? "Rupture" : isClosed ? "Fermé" : "Ajouter"}</span>
                    </button>
                  </div>
                );
              })}
            </div>

            {/* EMPTY STATE */}
            {produits.length === 0 && !isProductsLoading && (
              <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed rounded-3xl border-gray-100">
                <Search size={48} className="mb-4 text-gray-200" />
                <p className="font-bold text-gray-400">Aucun produit trouvé</p>
              </div>
            )}

            {/* PAGINATION */}
            <div className="flex items-center justify-between mt-12">
              <button 
                disabled={page <= 1} 
                onClick={() => setPage(p => p - 1)} 
                className="flex items-center gap-2 px-6 py-3 font-bold bg-white border border-gray-100 rounded-2xl disabled:opacity-50"
              >
                <ChevronLeft size={18} /> Précédent
              </button>
              <span className="font-bold text-gray-400">Page {page} / {totalPages}</span>
              <button 
                disabled={page >= totalPages} 
                onClick={() => setPage(p => p + 1)} 
                className="flex items-center gap-2 px-6 py-3 font-bold bg-white border border-gray-100 rounded-2xl disabled:opacity-50"
              >
                Suivant <ChevronRight size={18} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ✅ COMPOSANTS AUXILIAIRES
function PaymentBadge({ nomPaiement, numero }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!numero) return;
    navigator.clipboard.writeText(numero);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      disabled={!numero}
      className={`px-3 py-1.5 text-xs font-bold rounded-xl flex items-center gap-2 transition-all border ${
        copied
          ? "bg-emerald-50 text-emerald-600 border-emerald-200"
          : "bg-gray-50 text-gray-600 border-gray-100 hover:border-red-200 hover:bg-white active:scale-95"
      }`}
      title={numero ? "Copier le numéro" : ""}
    >
      <span>{nomPaiement}</span>
      {numero && (
        <>
          <span className={copied ? "text-emerald-600" : "text-[#f34141] font-black"}>
            {numero}
          </span>
          {copied ? (
            <Check size={14} strokeWidth={3} className="text-emerald-500" />
          ) : (
            <Copy size={14} className="text-gray-400" />
          )}
        </>
      )}
    </button>
  );
}

function InfoCard({ icon, label, children }) {
  return (
    <div className="flex items-center gap-4 p-5 bg-white border shadow-sm border-gray-50 rounded-3xl">
      <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-2xl">{icon}</div>
      <div>
        <p className="text-xs font-bold text-gray-400 uppercase">{label}</p>
        <div className="text-sm font-bold text-slate-800">{children}</div>
      </div>
    </div>
  );
}
